import type { ReactNode } from "react";
import { Box, IconButton, Link, TextField, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { Link as RouterLink } from "react-router-dom";
import type { CreditBalanceResult } from "../messaging/chat.api";
import { ChatContextPicker, type ChatSelectionContext } from "./ChatContextPicker";
import { NikolaComingSoon } from "./NikolaComingSoon";

export const ChatComposer = ({
  draft,
  context,
  fixedContextLabel,
  embedded = false,
  disabled,
  running = false,
  balance,
  blocked,
  blockedLabel = "Add credits",
  blockedHref = "/subscription?credits=1",
  blockedDetail,
  placeholder = "Ask Nikola about your games…",
  onChange,
  onContextChange,
  onSend,
  onCancel,
}: {
  draft: string;
  context: ChatSelectionContext | null;
  fixedContextLabel?: ReactNode;
  embedded?: boolean;
  disabled: boolean;
  running?: boolean;
  balance: CreditBalanceResult | null;
  estimatedCredits: number | null;
  blocked: boolean;
  blockedLabel?: string;
  blockedHref?: string;
  blockedDetail?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onContextChange: (value: ChatSelectionContext | null) => void;
  onSend: () => void;
  onCancel?: () => void;
}) => import.meta.env.VITE_NIKOLA_DISABLED === "true" ? <NikolaComingSoon /> : (
  <Box
    component="footer"
    data-testid="chat-composer"
    data-tour="chat-composer"
    sx={{
      position: embedded ? "static" : "sticky",
      bottom: embedded ? "auto" : 0,
      zIndex: 3,
      px: embedded ? 1 : { xs: 1, sm: 2 },
      pt: 1,
      pb: embedded ? 1 : "max(10px, env(safe-area-inset-bottom))",
      bgcolor: "background.default",
      borderTop: "1px solid",
      borderColor: "divider",
    }}
  >
    <Box
      sx={{
        maxWidth: 960,
        mx: "auto",
        border: "2px solid",
        borderColor: "primary.main",
        bgcolor: "rgba(240,236,226,.025)",
        boxShadow: { sm: "6px 6px 0 rgba(0,0,0,.25)" },
        "&:focus-within": { borderColor: "secondary.main" },
      }}
    >
      <TextField
        id="nikola-chat-input"
        multiline
        minRows={1}
        maxRows={embedded ? 4 : 9}
        fullWidth
        placeholder={placeholder}
        value={draft}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            onSend();
          }
        }}
        inputProps={{ "aria-label": "Message Nikola", "aria-describedby": "nikola-credit-note" }}
        sx={{
          "& .MuiOutlinedInput-root": {
            alignItems: "flex-start",
            minHeight: embedded ? 50 : 64,
            p: embedded ? 1 : { xs: 1.25, sm: 1.5 },
            borderRadius: 0,
            fontSize: embedded ? ".84rem" : { xs: ".92rem", sm: "1rem" },
            lineHeight: 1.55,
            "& fieldset": { border: 0 },
          },
          "& textarea": { overflowY: "auto!important" },
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: 1, p: 1, pt: 0 }}>
        {fixedContextLabel ? (
          <Box
            sx={{
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              color: "text.secondary",
              fontSize: ".6rem",
              fontWeight: 900,
              letterSpacing: ".08em",
            }}
          >
            {fixedContextLabel}
          </Box>
        ) : (
          <ChatContextPicker value={context} disabled={disabled} onChange={onContextChange} />
        )}
        <IconButton
          aria-label={running ? "Stop Nikola" : "Send message"}
          disabled={running ? !onCancel : disabled || !draft.trim()}
          onClick={running ? onCancel : onSend}
          sx={{
            flex: "0 0 auto",
            width: 46,
            height: 46,
            borderRadius: 0,
            bgcolor: "primary.main",
            color: "background.default",
            "&:hover": { bgcolor: "secondary.main" },
          }}
        >
          {running ? <StopRoundedIcon /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
    <Box
      id="nikola-credit-note"
      sx={{ display: "flex", justifyContent: "space-between", gap: 1, maxWidth: 960, mx: "auto", mt: 0.7 }}
    >
      <Typography sx={{ color: "text.secondary", fontSize: ".56rem" }}>Only pay for what Nikola uses</Typography>
      <Box sx={{ textAlign: "right" }}>
        <Typography sx={{ color: "text.secondary", fontSize: ".56rem", whiteSpace: "nowrap" }}>
          {balance ? `${balance.available} credits available` : "Credits unavailable"}
        </Typography>
        {blocked && (
          <>
            {balance?.available === 0 && (
              <Typography sx={{ color: "secondary.main", fontSize: ".62rem" }}>No credits available</Typography>
            )}
            <Link
              component={RouterLink}
              to={blockedHref}
              sx={{ minHeight: 24, color: "secondary.main", fontSize: ".62rem", fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {blockedLabel}
            </Link>
            {blockedDetail && (
              <Typography sx={{ color: "text.secondary", fontSize: ".54rem" }}>{blockedDetail}</Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  </Box>
);
